import { createContext, useContext, useEffect, useState, useCallback } from "react";

const AuthCtx = createContext(null);
const LS_USERS = "vpcl_users";
const LS_SESSION = "vpcl_session";

function loadUsers() {
  try { return JSON.parse(localStorage.getItem(LS_USERS) || "[]"); } catch { return []; }
}
function saveUsers(u) { localStorage.setItem(LS_USERS, JSON.stringify(u)); }

function loadSession() {
  try { return JSON.parse(localStorage.getItem(LS_SESSION) || "null"); } catch { return null; }
}
function saveSession(s) {
  if (s) localStorage.setItem(LS_SESSION, JSON.stringify(s));
  else localStorage.removeItem(LS_SESSION);
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => loadSession());
  const [ready, setReady] = useState(true);

  useEffect(() => {
    setReady(true);
  }, []);

  const signup = useCallback(({ name, email, password, company }) => {
    const users = loadUsers();
    const exists = users.find((u) => u.email.toLowerCase() === email.toLowerCase());
    if (exists) throw new Error("An account already exists with this email.");
    const newUser = {
      id: crypto.randomUUID(),
      name: name.trim(),
      email: email.trim().toLowerCase(),
      company: (company || "").trim(),
      password, // mock only — local storage
      createdAt: new Date().toISOString(),
      preferences: { newsletter: true, projectAlerts: true },
    };
    users.push(newUser);
    saveUsers(users);
    const session = { id: newUser.id, name: newUser.name, email: newUser.email, company: newUser.company, preferences: newUser.preferences };
    saveSession(session);
    setUser(session);
    return session;
  }, []);

  const login = useCallback(({ email, password }) => {
    const users = loadUsers();
    const found = users.find((u) => u.email.toLowerCase() === email.toLowerCase() && u.password === password);
    if (!found) throw new Error("Invalid email or password.");
    const session = { id: found.id, name: found.name, email: found.email, company: found.company, preferences: found.preferences };
    saveSession(session);
    setUser(session);
    return session;
  }, []);

  const logout = useCallback(() => {
    saveSession(null);
    setUser(null);
  }, []);

  const updateProfile = useCallback((patch) => {
    const users = loadUsers();
    const idx = users.findIndex((u) => u.id === user?.id);
    if (idx === -1) return;
    users[idx] = { ...users[idx], ...patch };
    saveUsers(users);
    const session = { id: users[idx].id, name: users[idx].name, email: users[idx].email, company: users[idx].company, preferences: users[idx].preferences };
    saveSession(session);
    setUser(session);
  }, [user]);

  return (
    <AuthCtx.Provider value={{ user, ready, signup, login, logout, updateProfile }}>
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => {
  const ctx = useContext(AuthCtx);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
};

export const initials = (name = "") =>
  name.split(/\s+/).filter(Boolean).slice(0, 2).map((s) => s[0].toUpperCase()).join("") || "U";
